import { useEffect, useMemo, useState } from 'react'
import { Box, Divider, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import { Virtuoso } from 'react-virtuoso'

import { primary } from '../../../theme/palette'
import { ORG_NODE_STYLE, type OrgNode, type OrgNodeKind } from '../../../lib/orgStructureTree'

export interface OrgTreeContextAction {
  type: 'add-child' | 'edit' | 'delete'
  node: OrgNode
}

interface Props {
  tree: OrgNode[]
  selectedId: string | null
  onSelect: (node: OrgNode) => void
  onContextAction: (action: OrgTreeContextAction) => void
  can: (kind: OrgNodeKind, action: 'create' | 'update' | 'delete') => boolean
  search?: string
}

const ADD_CHILD_LABEL: Record<OrgNodeKind, string | null> = {
  group: 'Add Sub Group',
  subgroup: 'Add Customer',
  customer: 'Add Site',
  site: null,
}

type MatchIds = { hits: Set<string>; visible: Set<string> }

interface FlatRow {
  node: OrgNode
  depth: number
}

/// Depth-first pre-order walk producing exactly the rows Virtuoso
/// should render — a node is included unless search excludes it, and
/// its children only get walked when the node is both expandable and
/// actually expanded.
function flattenVisible(nodes: OrgNode[], depth: number, expanded: Set<string>, matchIds: MatchIds | null, out: FlatRow[]) {
  for (const node of nodes) {
    if (matchIds && !matchIds.visible.has(node.id)) continue
    out.push({ node, depth })
    if (node.children.length > 0 && expanded.has(node.id)) {
      flattenVisible(node.children, depth + 1, expanded, matchIds, out)
    }
  }
}

/// Right-click-menu tree for the Group -> Sub Group -> Customer -> Site
/// hierarchy — same interaction pattern as `LocationTree.tsx`
/// (expand/collapse, `anchorPosition` context menu, add-child/edit/
/// delete) generalized from one node kind to four.
export function OrgStructureTree({ tree, selectedId, onSelect, onContextAction, can, search }: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(tree.map((n) => n.id)))
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; node: OrgNode } | null>(null)

  useEffect(() => {
    setExpanded((prev) => {
      const next = new Set(prev)
      tree.forEach((n) => next.add(n.id))
      return next
    })
  }, [tree])

  const matchIds = useMemo(() => {
    const q = search?.trim().toLowerCase()
    if (!q) return null
    const hits = new Set<string>()
    function walk(nodes: OrgNode[]) {
      for (const n of nodes) { if (n.name.toLowerCase().includes(q!)) hits.add(n.id); walk(n.children) }
    }
    walk(tree)
    const visible = new Set<string>()
    function mark(nodes: OrgNode[], ancestors: string[]): boolean {
      let any = false
      for (const n of nodes) {
        const childHit = mark(n.children, [...ancestors, n.id])
        if (hits.has(n.id) || childHit) { visible.add(n.id); ancestors.forEach((a) => visible.add(a)); any = true }
      }
      return any
    }
    mark(tree, [])
    return { hits, visible }
  }, [search, tree])

  // Auto-reveal matches the moment a new search term produces a new
  // match set — a one-time union into `expanded`, not a standing
  // override, so manual toggle/expand-all/collapse-all keep working
  // normally on top of it while search is active (see isExpanded
  // below, which no longer force-overrides during search).
  useEffect(() => {
    if (!matchIds) return
    setExpanded((prev) => {
      const next = new Set(prev)
      matchIds.visible.forEach((id) => next.add(id))
      return next
    })
  }, [matchIds])

  const rows = useMemo(() => {
    const out: FlatRow[] = []
    flattenVisible(tree, 0, expanded, matchIds, out)
    return out
  }, [tree, expanded, matchIds])

  function toggle(id: string) {
    setExpanded((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next })
  }

  function expandAll() {
    const all = new Set<string>()
    function walk(nodes: OrgNode[]) {
      for (const n of nodes) { if (n.children.length > 0) all.add(n.id); walk(n.children) }
    }
    walk(tree)
    setExpanded(all)
  }

  function collapseAll() {
    setExpanded(new Set())
  }

  function handleAction(type: OrgTreeContextAction['type']) {
    if (ctxMenu) onContextAction({ type, node: ctxMenu.node })
    setCtxMenu(null)
  }

  const addChildLabel = ctxMenu ? ADD_CHILD_LABEL[ctxMenu.node.kind] : null
  const canAddChild = ctxMenu ? can(ctxMenu.node.kind, 'create') : false
  const canEdit = ctxMenu ? can(ctxMenu.node.kind, 'update') : false
  const canDelete = ctxMenu ? can(ctxMenu.node.kind, 'delete') : false

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" spacing={0.5}
        sx={{ px: 1.5, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Tooltip title="Expand all" arrow>
          <Box component="button" type="button" onClick={expandAll} aria-label="Expand all"
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5, border: 'none', background: 'none',
              cursor: 'pointer', px: 0.75, py: 0.5, borderRadius: 1, color: 'text.secondary',
              fontSize: 11.5, fontWeight: 600, fontFamily: 'inherit',
              '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
            }}>
            <UnfoldMoreIcon sx={{ fontSize: 15 }} /> Expand all
          </Box>
        </Tooltip>
        <Tooltip title="Collapse all" arrow>
          <Box component="button" type="button" onClick={collapseAll} aria-label="Collapse all"
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5, border: 'none', background: 'none',
              cursor: 'pointer', px: 0.75, py: 0.5, borderRadius: 1, color: 'text.secondary',
              fontSize: 11.5, fontWeight: 600, fontFamily: 'inherit',
              '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
            }}>
            <UnfoldLessIcon sx={{ fontSize: 15 }} /> Collapse all
          </Box>
        </Tooltip>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0 }}>
      {rows.length === 0 ? (
        <Box sx={{ px: 3, py: 6, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {search ? 'No matches.' : 'No Groups or Customers yet.'}
          </Typography>
        </Box>
      ) : (
        <Virtuoso
          style={{ height: '100%' }}
          data={rows}
          increaseViewportBy={200}
          computeItemKey={(_, row) => row.node.id}
          itemContent={(_, row) => (
            <OrgTreeRow node={row.node} depth={row.depth} expanded={expanded} onToggle={toggle}
              selectedId={selectedId} onSelect={onSelect}
              onContextMenu={(e, n) => { e.preventDefault(); e.stopPropagation(); setCtxMenu({ x: e.clientX, y: e.clientY, node: n }) }} />
          )}
        />
      )}
      </Box>

      <Menu
        open={Boolean(ctxMenu)}
        onClose={() => setCtxMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={ctxMenu ? { top: ctxMenu.y, left: ctxMenu.x } : undefined}
        slotProps={{ paper: { sx: { minWidth: 190, borderRadius: 1.5 } } }}
      >
        {addChildLabel && (
          <MenuItem disabled={!canAddChild} onClick={() => handleAction('add-child')} sx={{ fontSize: 13 }}>
            <AddIcon sx={{ fontSize: 16, mr: 1.5, color: 'text.secondary' }} /> {addChildLabel}
          </MenuItem>
        )}
        <MenuItem disabled={!canEdit} onClick={() => handleAction('edit')} sx={{ fontSize: 13 }}>
          <EditOutlinedIcon sx={{ fontSize: 16, mr: 1.5, color: 'text.secondary' }} /> Edit
        </MenuItem>
        <Divider />
        <MenuItem disabled={!canDelete} onClick={() => handleAction('delete')} sx={{ fontSize: 13, color: 'error.main' }}>
          <DeleteOutlineIcon sx={{ fontSize: 16, mr: 1.5 }} /> Delete
        </MenuItem>
      </Menu>
    </Box>
  )
}

function OrgTreeRow({ node, depth, expanded, onToggle, selectedId, onSelect, onContextMenu }: {
  node: OrgNode; depth: number
  expanded: Set<string>; onToggle: (id: string) => void
  selectedId: string | null; onSelect: (node: OrgNode) => void
  onContextMenu: (e: React.MouseEvent, node: OrgNode) => void
}) {
  const hasChildren = node.children.length > 0
  const isExpanded = expanded.has(node.id)
  const isSelected = node.id === selectedId
  const { icon: Icon, color } = ORG_NODE_STYLE[node.kind]

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1}
        onClick={() => { onSelect(node); if (hasChildren && !isExpanded) onToggle(node.id) }}
        onContextMenu={(e) => onContextMenu(e, node)}
        sx={{
          position: 'relative', pl: 1.5 + depth * 2.5, pr: 1.5, py: 1, cursor: 'pointer',
          transition: 'background-color 120ms ease',
          bgcolor: isSelected ? primary.selected : 'transparent',
          '&:hover': { bgcolor: isSelected ? primary.selectedHover : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.035)' },
          '&::before': isSelected ? { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: 'primary.main' } : undefined,
        }}
      >
        <Box onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggle(node.id) }}
          sx={{ width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: 'text.secondary', flexShrink: 0, opacity: hasChildren ? 1 : 0,
            transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 150ms ease' }}>
          <ChevronRightIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{
          width: 24, height: 24, borderRadius: 1, flexShrink: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: `${color}1A`, color,
        }}>
          <Icon sx={{ fontSize: 15 }} />
        </Box>
        <Tooltip title={node.name} arrow>
          <Typography sx={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: isSelected ? 600 : 500, letterSpacing: '-0.005em' }} noWrap>
            {node.name}
          </Typography>
        </Tooltip>
        {hasChildren && <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', opacity: 0.7 }}>{node.children.length}</Typography>}
      </Stack>
    </Box>
  )
}
